import { Button } from "primereact/button";
import { InputText } from "primereact/inputtext";
import { Password } from "primereact/password";
import * as React from "react";
import { useNavigate } from "react-router";
import { Message } from "primereact/message";
import { useAppDispatch, useAppSelector } from "../redux/store.ts";
import { userSlice } from "../redux/slices/user.ts";
import { api } from "../api";

export default function Login() {
  const [email, setEmail] = React.useState("");
  const [password, setPassword] = React.useState("");
  const [error, setError] = React.useState<string | null>(null);
  const [loading, setLoading] = React.useState(false);

  const dispatch = useAppDispatch();
  const { accessToken } = useAppSelector((state) => state[userSlice.name]);
  const {
    data: getMeData,
    isFetching: getMeIsFetching,
    error: getMeError,
  } = api.endpoints.getCurrentUserInfoApiMeGet.useQuery(undefined, {
    skip: !accessToken,
  });
  const [triggerLogin] =
    api.endpoints.loginForAccessTokenApiTokenPost.useMutation();

  const navigate = useNavigate();

  const getMeDataIfSuccess = React.useMemo(
    () => (!getMeError && !getMeIsFetching && getMeData ? getMeData : null),
    [getMeError, getMeIsFetching, getMeData],
  );

  const handleLoginSuccessful = React.useCallback(() => {
    setLoading(true);
    try {
      const navigateResult = navigate("/zlecenia-transportowe");
      if (navigateResult instanceof Promise) {
        navigateResult
          .then(() => {})
          .catch((e) => {
            console.error(e);
            setLoading(false);
          });
      }
    } catch (e) {
      console.error(e);
      setLoading(false);
    }
  }, [navigate]);

  // Check if user is already logged in on component mount
  React.useEffect(() => {
    if (getMeDataIfSuccess != null) {
      dispatch(userSlice.actions.setUser(getMeDataIfSuccess.user));
      handleLoginSuccessful();
    }
  }, [handleLoginSuccessful, dispatch, getMeDataIfSuccess]);

  const handleSubmit = React.useCallback(
    async (e: React.FormEvent) => {
      // Prevent default form submission behavior
      e.preventDefault();

      // Clear any previous errors
      setError(null);

      // Validate inputs
      if (!email || !password) {
        setError("Wprowadź adres e-mail i hasło");
        return;
      }

      setLoading(true);

      // Call the login API endpoint
      const { data, error } = await triggerLogin({
        tokenRequest: {
          email,
          password,
        },
      });

      if (error) {
        console.error("Login error:", error);
        setPassword("");
        setError("Logowanie nie powiodło się.");
      } else {
        dispatch(userSlice.actions.setToken(data.access_token));
        api.util.resetApiState();
        handleLoginSuccessful();
      }
      setLoading(false);
    },
    [dispatch, email, handleLoginSuccessful, password, triggerLogin],
  );

  return (
    <div className="login">
      <div className="login-logo">
        <img src="assets/waybiller-logo-white.svg" alt="logo" />
      </div>
      <div className="login-container">
        <h1>Logowanie</h1>
        <form
          onSubmit={(e) => {
            void handleSubmit(e);
          }}
          className="login-fields"
        >
          {error && (
            <Message
              severity="error"
              text={error}
              style={{ width: "100%", marginBottom: "1rem" }}
            />
          )}
          <InputText
            value={email}
            keyfilter="email"
            placeholder="Adres e-mail"
            onChange={(e) => setEmail(e.target.value)}
            id="email"
            name="email"
            disabled={getMeIsFetching || loading}
          />
          <Password
            value={password}
            placeholder="Hasło"
            feedback={false}
            toggleMask
            onChange={(e) => setPassword(e.target.value)}
            id="password"
            name="password"
            disabled={getMeIsFetching || loading}
          />
          <Button
            type="submit"
            label={
              getMeIsFetching
                ? "Sprawdzanie statusu logowania..."
                : "Zaloguj się"
            }
            loading={getMeIsFetching || loading}
            disabled={getMeIsFetching || loading}
          />
        </form>
      </div>
    </div>
  );
}
